import { useEffect, useState } from 'react';
import { socialApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { StatCard } from '@/components/ui/StatCard';
import { KpiGrid } from '@/components/ui/KpiGrid';
import type { SocialAnalyticsDashboard, SocialMonitoringSummary } from '@/types/social';
import { showApiError } from '@/utils/apiError';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import MessageIcon from '@mui/icons-material/Message';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { notify } from '@/core/toast/notify';
import { SocialMiniCharts } from '../components/SocialMiniCharts';
import { PageSection } from '@/components/ui/PageSection';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface SocialAnalyticsCenterPageProps {
  embedded?: boolean;
}

export function SocialAnalyticsCenterPage({ embedded }: SocialAnalyticsCenterPageProps) {
  const [analytics, setAnalytics] = useState<SocialAnalyticsDashboard | null>(null);
  const [monitoring, setMonitoring] = useState<SocialMonitoringSummary | null>(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    socialApi.analyticsDashboard().then(setAnalytics).catch((err) => showApiError(err, 'Analytics unavailable'));
    socialApi.monitoringSummary().then(setMonitoring).catch(() => undefined);
  };

  useEffect(() => {
    load();
  }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      await socialApi.syncAnalytics();
      notify.success('Analytics refreshed');
      load();
    } catch (err) {
      showApiError(err, 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const body = (
    <>
      <KpiGrid columns={4}>
        <StatCard title="Followers / audience" value={analytics?.followers?.toLocaleString() ?? '—'} icon={<TrendingUpIcon />} />
        <StatCard title="Reach" value={analytics?.reach?.toLocaleString() ?? '—'} icon={<VisibilityIcon />} />
        <StatCard title="Engagement" value={analytics?.engagement?.toLocaleString() ?? '—'} icon={<TouchAppIcon />} />
        <StatCard title="Messages" value={analytics?.messages ?? '—'} icon={<MessageIcon />} />
        <StatCard
          title="Response time"
          value={analytics?.avgResponseMinutes != null ? `${analytics.avgResponseMinutes} min` : '—'}
          icon={<ScheduleIcon />}
        />
        <StatCard title="Clicks" value={analytics?.clicks?.toLocaleString() ?? '—'} icon={<TouchAppIcon />} />
        <StatCard title="Impressions" value={analytics?.impressions?.toLocaleString() ?? '—'} icon={<VisibilityIcon />} />
        <StatCard title="Lead attribution" value="CRM" icon={<TrendingUpIcon />} />
      </KpiGrid>

      <SocialMiniCharts trend={analytics?.engagementTrend} channels={analytics?.channelPerformance} />

      {monitoring && (
        <PageSection title="Live monitoring">
          <div className="card grid gap-2 p-4 text-sm sm:grid-cols-2 lg:grid-cols-5">
            <div>Unread: {monitoring.unreadMessages}</div>
            <div>Pending replies: {monitoring.pendingReplies}</div>
            <div>Escalated: {monitoring.escalatedConversations}</div>
            <div>High priority: {monitoring.highPriorityMentions}</div>
            <div>Open reviews: {monitoring.openReviews}</div>
          </div>
        </PageSection>
      )}

      <PageSection title="Top content" description="Ranked by engagement from synced snapshots">
        <div className="card p-4 text-sm text-slate-500">
          Run Refresh data after publishing to pull network metrics. Per-post likes/comments/shares appear in Publishing logs and platform workspaces.
        </div>
      </PageSection>
    </>
  );

  if (embedded) return <div className="space-y-4">{body}</div>;

  return (
    <div>
      <PageHeader
        title="Analytics Center"
        description="Cross-platform performance, exports, and scheduled reporting"
        breadcrumbs={[{ label: 'Social', to: '/social' }, { label: 'Analytics' }]}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={async () => {
                try {
                  const res = await socialApi.exportPostsCsv();
                  downloadBlob(res.data, 'social-posts.csv');
                } catch (err) {
                  showApiError(err, 'Export failed');
                }
              }}
            >
              Export posts CSV
            </Button>
            <Button
              variant="secondary"
              onClick={async () => {
                try {
                  const res = await socialApi.exportInboxCsv();
                  downloadBlob(res.data, 'social-inbox.csv');
                } catch (err) {
                  showApiError(err, 'Export failed');
                }
              }}
            >
              Export inbox CSV
            </Button>
            <Button variant="secondary" onClick={() => notify.info('Scheduled reports — configure in Settings')}>
              Schedule report
            </Button>
            <Button onClick={() => void sync()} loading={syncing}>
              Refresh data
            </Button>
          </div>
        }
      />
      {body}
    </div>
  );
}
